import React, { useEffect, useState } from "react";
import InboxView from "../../components/InboxView";
import { circlesApi } from "./circlesApi";
import { Modal, StatusBadge, inr, phoneText, shortDate, useBusy, useCirclesList } from "./circlesShared";
import "../../assets/css/PageHeading.css";

const TABS = [
  { id: "Pending", label: "Waiting for approval", tone: "#d97706" },
  { id: "Active", label: "Active", tone: "#16a34a" },
  { id: "Suspended", label: "Suspended", tone: "#dc2626" },
  { id: "Rejected", label: "Rejected", tone: "#64748b" },
  { id: "", label: "All", tone: "#1e3a8a" },
];

function StatusModal({ agent, status, onClose, onDone }) {
  const [note, setNote] = useState("");
  const { busy, error, run } = useBusy();
  const verb = { Suspended: "Suspend", Rejected: "Reject", Active: "Reactivate" }[status];
  const submit = () =>
    run(async () => {
      await circlesApi.agentStatus(agent.Agent_Id, status, note);
      onDone();
    });
  return (
    <Modal
      title={`${verb} ${agent.Name}`}
      onClose={onClose}
      busy={busy}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="wl-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className={`wl-btn ${status === "Active" ? "wl-btn--primary" : "wl-btn--danger"}`} disabled={busy}>
            {verb}
          </button>
        </>
      }
    >
      {status === "Suspended" ? <p className="cx-hint">A suspended agent earns nothing from sales converted while suspended, and is skipped as a sponsor.</p> : null}
      <label className="wl-field">
        <span>Reason {status === "Active" ? "(optional)" : "(kept on the agent's record)"}</span>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}
    </Modal>
  );
}

export default function CirclesAgents() {
  const [tab, setTab] = useState("Pending");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [requestsOnly, setRequestsOnly] = useState(false);
  const [changing, setChanging] = useState(null);
  const [notice, setNotice] = useState(null);
  const { busy, error: actionError, run } = useBusy();

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { rows, extra, loading, error, reload } = useCirclesList(
    () => circlesApi.agents({ status: tab || undefined, q: query || undefined, domain_requests: requestsOnly ? 1 : undefined }),
    [tab, query, requestsOnly]
  );

  const approve = (a) =>
    run(async () => {
      const res = await circlesApi.approveAgent(a.Agent_Id);
      setNotice({ type: "success", text: `${a.Name} is approved with referral code ${res.data.Ref_Code}.` });
      reload();
    });
  const decideDomain = (a, d, approveIt) =>
    run(async () => {
      await circlesApi.agentDomain(a.Agent_Id, d.Domain_Id, approveIt);
      setNotice({ type: "success", text: `${d.Name} ${approveIt ? "approved" : "removed"} for ${a.Name}.` });
      reload();
    });

  const domains = (a) => (
    <div className="cx-chips">
      {a.Domains.length === 0 ? <span className="wl-sub">None chosen yet</span> : null}
      {a.Domains.map((d) =>
        d.Status === "Requested" ? (
          <span key={d.Domain_Id} className="cx-badge cx-badge--warning">
            {d.Name}?{" "}
            <button type="button" className="wl-link-btn" disabled={busy} onClick={() => decideDomain(a, d, true)}>
              Approve
            </button>{" "}
            <button type="button" className="wl-link-btn" disabled={busy} onClick={() => decideDomain(a, d, false)}>
              Remove
            </button>
          </span>
        ) : (
          <span key={d.Domain_Id} className="cx-badge cx-badge--neutral">
            {d.Name}
          </span>
        )
      )}
    </div>
  );

  const actions = (a) => (
    <div className="cx-actions">
      {a.Status === "Pending" ? (
        <>
          <button type="button" className="wl-btn wl-btn--primary wl-btn--sm" disabled={busy} onClick={() => approve(a)}>
            Approve
          </button>
          <button type="button" className="wl-btn wl-btn--sm" onClick={() => setChanging({ agent: a, status: "Rejected" })}>
            Reject
          </button>
        </>
      ) : null}
      {a.Status === "Active" ? (
        <button type="button" className="wl-btn wl-btn--sm" onClick={() => setChanging({ agent: a, status: "Suspended" })}>
          Suspend
        </button>
      ) : null}
      {a.Status === "Suspended" ? (
        <button type="button" className="wl-btn wl-btn--sm" onClick={() => setChanging({ agent: a, status: "Active" })}>
          Reactivate
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="p-2 p-md-3">
      <div className="co-page-head">
        <h2 className="co-page-head__title">Circles Agents</h2>
      </div>
      <InboxView
        tabs={TABS}
        counts={extra.counts || {}}
        tab={tab}
        onTab={setTab}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search name, mobile, code or city"
        onRefresh={reload}
        toolbarExtra={
          <label className="cx-check wl-sub">
            <input type="checkbox" checked={requestsOnly} onChange={(e) => setRequestsOnly(e.target.checked)} />
            Business type requests
          </label>
        }
        notice={notice}
        onDismissNotice={() => setNotice(null)}
        error={error || actionError}
        loading={loading}
        rows={rows}
        rowKey={(a) => a.Agent_Id}
        empty="No agents here."
        columns={[
          { key: "agent", label: "Agent", render: (a) => (<><div className="wl-name">{a.Name}</div><div className="wl-sub cx-mono">{a.Ref_Code || "No code yet"}</div></>) },
          { key: "contact", label: "Contact", render: (a) => (<><div>{phoneText(a.Mobile)}</div><div className="wl-sub">{a.Email || "—"}{a.City ? ` · ${a.City}` : ""}</div></>) },
          { key: "domains", label: "Business types", render: domains },
          { key: "work", label: "Leads · earned", render: (a) => `${a.Leads} · ${inr(a.Earned)}` },
          { key: "kyc", label: "KYC", render: (a) => (a.Has_PAN && a.Has_Payout_Details ? "Complete" : a.Has_PAN || a.Has_Payout_Details ? "Partial" : "Missing") },
          { key: "status", label: "Status", render: (a) => (<><StatusBadge status={a.Status} /><div className="wl-sub">Joined {shortDate(a.Joined_On)}</div></>) },
          { key: "actions", label: "Actions", className: "wl-actions-col", render: actions },
        ]}
        mobileCard={(a) => ({
          title: a.Name,
          meta: [{ label: a.Status, tone: a.Status === "Active" ? "success" : a.Status === "Pending" ? "warning" : "neutral" }],
          fields: [
            { label: "Code", value: a.Ref_Code || "No code yet" },
            { label: "Mobile", value: phoneText(a.Mobile) },
            { label: "Business types", value: a.Domains.map((d) => `${d.Name}${d.Status === "Requested" ? " (requested)" : ""}`).join(", ") },
            { label: "Leads · earned", value: `${a.Leads} · ${inr(a.Earned)}` },
          ],
          actions: actions(a),
        })}
      />
      {changing ? (
        <StatusModal
          agent={changing.agent}
          status={changing.status}
          onClose={() => setChanging(null)}
          onDone={() => {
            setNotice({ type: "success", text: `${changing.agent.Name} is now ${changing.status.toLowerCase()}.` });
            setChanging(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}
